import {AutoComplete, ConfigProvider, Input, Spin, Image} from "antd";
import {useNavigate} from "react-router-dom";
import styles from "./user_landing.module.css";
import {useCallback, useState} from "react";
import {useAuth} from "../../../context/AppAuthContext";
import {FileImageOutlined} from "@ant-design/icons";

const {Search} = Input;

export default function UserLanding() {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [options, setOptions] = useState([]);
    const {searchListingsLanding} = useAuth();

    function handleLogin() {
        navigate("/login");
    }

    const fetchSearchSuggestions = async (query) => {
        try {
            setIsSearching(true);
            const response = await searchListingsLanding(query);

            console.log("Search response:", response);
            if(response.status === 401) {
                handleLogin();
            }
            const data = response.data;

            if (!Array.isArray(data)) {
                throw new Error("Invalid response format");
            }

            const formattedOptions = data.map(item => ({
                value: item.title,
                label: (
                    <div
                        className={styles.dropdownItem}
                        onClick={() => navigateToListing(item.id)}
                    >
                        <div className={styles.imageContainer}>
                            {
                                item.image ?
                                <Image
                                    src={item?.image?.includes('base64,') ? item.image : `data:image/png;base64,${item.image}`}
                                    alt={item.title}
                                    width={50}
                                    height={50}
                                    preview={false}
                                    className={styles.listingImage}
                                /> :
                                <FileImageOutlined/>
                            }
                        </div>
                        <div className={styles.textContainer}>
                            <span className={styles.dropdownTitle}>{item.title}</span>
                            {item.description && (
                                <span className={styles.dropdownDescription}>
                                    {item.description.substring(0, 60)}...
                                </span>
                            )}
                        </div>
                    </div>
                ),
                id: item.id
            }));

            setOptions(formattedOptions);
        } catch (error) {
            console.error("Search error:", error);
            setOptions([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Debounce function with cleanup
    const debounce = (func, delay) => {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => func.apply(this, args), delay);
        };
    };

    // Debounced search with dependency on searchListingsLanding
    const debouncedSearch = useCallback(
        debounce((query) => {
            if (query.trim().length > 2) {
                fetchSearchSuggestions(query);
            } else {
                setOptions([]);
            }
        }, 300),
        [searchListingsLanding]
    );

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        debouncedSearch(value);
    };

    const navigateToListing = (listingId) => {
        navigate(`/listing/${listingId}`);
    };

    const handleSearchSubmit = (value) => {
        if (value.trim() !== "") {
            navigate(`/search?q=${encodeURIComponent(value)}`);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.background}>
                <img
                    className={styles.backgroundImage}
                    src="/resources/images/backgrounds/getting_started_background.png"
                    alt="Background"
                    draggable={false}
                />
            </div>
            <div className={styles.overlay}>
                <p className={styles.getting_started_title}>
                    Exchange or give away your old stuff
                </p>
                <div className={styles.search_area}>
                    <ConfigProvider
                        theme={{
                            token: {
                                colorPrimary: '#165251',
                                borderRadius: 8,
                                controlHeightLG: 48,
                            },
                            components: {
                                AutoComplete: {
                                    dropdownHeight: 400,
                                },
                            },
                        }}
                    >
                        <AutoComplete
                            popupClassName={styles.searchDropdown}
                            options={options}
                            autoFocus
                            onSelect={(value, option) => navigateToListing(option.id)}
                            onSearch={handleSearchChange}
                            value={searchQuery}
                            style={{width: "100%"}}
                            notFoundContent={isSearching ? <Spin size="small"/> : "No results found"}
                        >
                            <Search
                                placeholder="Explore Listings..."
                                allowClear
                                enterButton={
                                    isSearching ? (
                                        <Spin size="small" style={{color: 'white'}}/>
                                    ) : (
                                        "Search"
                                    )
                                }
                                size="large"
                                loading={isSearching}
                                onSearch={handleSearchSubmit}
                            />
                        </AutoComplete>
                    </ConfigProvider>
                </div>
            </div>
        </div>
    );
}